import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  APIMethod,
  ContentType,
  GraphQLConfig,
  MCPTool,
  NormalizedParameters,
  RequestConfig,
  ParameterConfig,
} from '@/types/mcpTool';
import { parseEndpoint } from '@/utils/endpointParser';
import { ParameterBuilder } from './ParameterBuilder';
import { RequestBodyBuilder as RequestBodyBuilderNew } from './RequestBodyBuilderNew';
import { GraphQLQueryBuilder } from './GraphQLQueryBuilder';
import { ContentTypeSelector } from './ContentTypeSelector';
import { Info, Zap } from 'lucide-react';

interface MethodAwareRequestBuilderProps {
  tool: MCPTool;
  endpoint: string;
  method: APIMethod;
  onValuesChange: (
    values: NormalizedParameters,
    pathConfigs: Record<string, ParameterConfig>,
    queryConfigs: Record<string, ParameterConfig>,
    bodyConfigs: Record<string, ParameterConfig>,
    graphqlConfig?: GraphQLConfig,
    requestConfig?: RequestConfig
  ) => void;
  onEndpointChange?: (newEndpoint: string) => void; // NEW: Callback to update endpoint
  initialValues?: NormalizedParameters;
  initialPathConfigs?: Record<string, ParameterConfig>;
  initialQueryConfigs?: Record<string, ParameterConfig>;
  initialBodyConfigs?: Record<string, ParameterConfig>;
  initialGraphQLConfig?: GraphQLConfig;
  initialRequestConfig?: RequestConfig;
}

/**
 * MethodAwareRequestBuilder Component (Enhanced)
 *
 * Dynamic request builder with parameter configuration
 * Features:
 * - Path parameters with source/fallback configuration
 * - Body/query parameters with AI/Human handoff options
 * - Method-aware forms
 * - No JSON mode (form builder only)
 */
export const MethodAwareRequestBuilder: React.FC<MethodAwareRequestBuilderProps> = ({
  tool,
  endpoint,
  method,
  onValuesChange,
  onEndpointChange,
  initialValues,
  initialPathConfigs,
  initialQueryConfigs,
  initialBodyConfigs,
  initialGraphQLConfig,
  initialRequestConfig,
}) => {
  const [pathParamValues, setPathParamValues] = useState<Record<string, any>>(
    initialValues?.path_params || {}
  );
  const [pathParamConfigs, setPathParamConfigs] = useState<Record<string, ParameterConfig>>(
    initialPathConfigs || {}
  );
  const [queryParamValues, setQueryParamValues] = useState<Record<string, any>>(
    initialValues?.query_params || {}
  );
  const [queryParamConfigs, setQueryParamConfigs] = useState<Record<string, ParameterConfig>>(
    initialQueryConfigs || {}
  );
  const [bodyValues, setBodyValues] = useState<Record<string, any>>(
    initialValues?.body || {}
  );
  const [bodyConfigs, setBodyConfigs] = useState<Record<string, ParameterConfig>>(
    initialBodyConfigs || {}
  );
  const [graphqlConfig, setGraphQLConfig] = useState<GraphQLConfig>(
    initialGraphQLConfig || { query: '', variables: undefined, operationName: undefined }
  );
  const [contentType, setContentType] = useState<ContentType>(
    initialRequestConfig?.content_type || 'application/json'
  );

  // Initialize values
  useEffect(() => {
    if (initialValues) {
      setPathParamValues(initialValues.path_params || {});
      setQueryParamValues(initialValues.query_params || {});
      setBodyValues(initialValues.body || {});
    }
    if (initialPathConfigs) {
      setPathParamConfigs(initialPathConfigs);
    }
    if (initialQueryConfigs) {
      setQueryParamConfigs(initialQueryConfigs);
    }
    if (initialBodyConfigs) {
      setBodyConfigs(initialBodyConfigs);
    }
    // Sync GraphQL config from props
    if (initialGraphQLConfig) {
      setGraphQLConfig(initialGraphQLConfig);
    }
    // Sync request config from props
    if (initialRequestConfig) {
      setContentType(initialRequestConfig.content_type || 'application/json');
    }
  }, [initialValues, initialPathConfigs, initialQueryConfigs, initialBodyConfigs, initialGraphQLConfig, initialRequestConfig]);

  // Emit values whenever they change
  useEffect(() => {
    const normalizedParams: NormalizedParameters = {
      path_params: pathParamValues,
      query_params: queryParamValues,
      body: method === 'GET' || method === 'DELETE' || method === 'GRAPHQL' ? null : bodyValues,
    };

    const requestConfig: RequestConfig = {
      content_type: contentType,
      body_format: contentType === 'application/json' ? 'json' :
                   contentType === 'application/x-www-form-urlencoded' ? 'form_data' : 'raw',
    };

    onValuesChange(
      normalizedParams,
      pathParamConfigs,
      queryParamConfigs,
      bodyConfigs,
      method === 'GRAPHQL' ? graphqlConfig : undefined,
      method !== 'GET' && method !== 'GRAPHQL' ? requestConfig : undefined
    );
  }, [pathParamValues, pathParamConfigs, queryParamValues, queryParamConfigs, bodyValues, bodyConfigs, graphqlConfig, contentType, method]);

  // Parse endpoint to extract parameters
  const { pathParams, queryParams } = parseEndpoint(endpoint);

  // For GraphQL, use a stable calculation that prioritizes initial values
  const graphqlParams = method === 'GRAPHQL'
    ? Object.keys(initialValues?.path_params || pathParamValues || {})
    : [];

  // Sync: When endpoint changes, merge auto-parsed params with existing manually-added params
  useEffect(() => {
    // For path params: add any new params from endpoint that don't exist yet
    const newPathValues = { ...pathParamValues };
    const newPathConfigs = { ...pathParamConfigs };
    let pathUpdated = false;

    pathParams.forEach(param => {
      if (!(param in newPathValues)) {
        newPathValues[param] = '';
        newPathConfigs[param] = {
          source: 'direct_input',
          fallback: 'error',
          required: false,
          let_ai_decide: false,
          handoff_to_human: false,
        };
        pathUpdated = true;
      }
    });

    if (pathUpdated) {
      setPathParamValues(newPathValues);
      setPathParamConfigs(newPathConfigs);
    }

    // For query params: add any new params from endpoint that don't exist yet
    const newQueryValues = { ...queryParamValues };
    const newQueryConfigs = { ...queryParamConfigs };
    let queryUpdated = false;

    queryParams.forEach(param => {
      if (!(param in newQueryValues)) {
        newQueryValues[param] = '';
        newQueryConfigs[param] = {
          source: 'direct_input',
          fallback: 'error',
          required: false,
          let_ai_decide: false,
          handoff_to_human: false,
        };
        queryUpdated = true;
      }
    });

    if (queryUpdated) {
      setQueryParamValues(newQueryValues);
      setQueryParamConfigs(newQueryConfigs);
    }
  }, [endpoint]); // Only run when endpoint changes

  const handlePathParamsChange = (values: Record<string, any>, configs: Record<string, ParameterConfig>) => {
    setPathParamValues(values);
    setPathParamConfigs(configs);
  };

  const handleQueryParamsChange = (values: Record<string, any>, configs: Record<string, ParameterConfig>) => {
    setQueryParamValues(values);
    setQueryParamConfigs(configs);
  };

  const handleBodyChange = (values: Record<string, any>, configs: Record<string, ParameterConfig>) => {
    setBodyValues(values);
    setBodyConfigs(configs);
  };

  const getMethodBadgeColor = () => {
    switch (method) {
      case 'GET':
        return 'bg-blue-100 text-blue-700';
      case 'POST':
        return 'bg-green-100 text-green-700';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-700';
      case 'DELETE':
        return 'bg-red-100 text-red-700';
      case 'PATCH':
        return 'bg-purple-100 text-purple-700';
      case 'GRAPHQL':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Method Badge */}
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Request Configuration for:</span>
        <Badge className={`${getMethodBadgeColor()} font-mono`}>
          {method}
        </Badge>
      </div>

      {/* GraphQL Flow */}
      {method === 'GRAPHQL' && (
        <>
          {/* GraphQL Parameters (if any defined) */}
          {graphqlParams.length > 0 && (
            <Card className="p-4 mb-4 overflow-x-hidden max-w-full">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-600" />
                GraphQL Query Parameters
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                These parameters will be used in your GraphQL variables with template syntax like <code className="bg-gray-100 px-1 rounded">{`{{param_name}}`}</code>
              </p>
              <ParameterBuilder
                type="path"
                values={pathParamValues}
                configs={pathParamConfigs}
                onChange={handlePathParamsChange}
                endpointParams={graphqlParams}
              />
            </Card>
          )}

          {/* GraphQL Query Builder */}
          <Card className="p-4 overflow-x-hidden max-w-full">
            <GraphQLQueryBuilder
              value={graphqlConfig}
              onChange={setGraphQLConfig}
            />
          </Card>
        </>
      )}

      {/* REST Flow */}
      {method !== 'GRAPHQL' && (
        <>
          {/* Path Parameters */}
          <Card className="p-4">
            <ParameterBuilder
              type="path"
              values={pathParamValues}
              configs={pathParamConfigs}
              onChange={handlePathParamsChange}
              endpointParams={pathParams}
              currentEndpoint={endpoint}
              onEndpointUpdate={onEndpointChange}
            />
          </Card>

          <Separator />

          {/* Query Parameters for GET */}
          {method === 'GET' && (
            <Card className="p-4">
              <ParameterBuilder
                type="query"
                values={queryParamValues}
                configs={queryParamConfigs}
                onChange={handleQueryParamsChange}
                endpointParams={queryParams}
                currentEndpoint={endpoint}
                onEndpointUpdate={onEndpointChange}
              />
            </Card>
          )}

          {/* POST/PUT/PATCH Request */}
          {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
            <>
              <Card className="p-4">
                <ContentTypeSelector
                  value={contentType}
                  onChange={setContentType}
                />
              </Card>

              <Card className="p-4">
                <RequestBodyBuilderNew
                  fields={[]}
                  values={bodyValues}
                  configs={bodyConfigs}
                  onChange={handleBodyChange}
                  method={method}
                />
              </Card>

              {/* Query Parameters for POST/PUT/PATCH */}
              <Card className="p-4">
                <ParameterBuilder
                  type="query"
                  values={queryParamValues}
                  configs={queryParamConfigs}
                  onChange={handleQueryParamsChange}
                  endpointParams={queryParams}
                  currentEndpoint={endpoint}
                  onEndpointUpdate={onEndpointChange}
                />
              </Card>
            </>
          )}

          {/* DELETE Request */}
          {method === 'DELETE' && (
            <>
              <Alert className="border-orange-200 bg-orange-50">
                <Info className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-900 text-sm">
                  DELETE requests typically don't require a body. Add query parameters below if needed.
                </AlertDescription>
              </Alert>

              <Card className="p-4">
                <ParameterBuilder
                  type="query"
                  values={queryParamValues}
                  configs={queryParamConfigs}
                  onChange={handleQueryParamsChange}
                  endpointParams={queryParams}
                  currentEndpoint={endpoint}
                  onEndpointUpdate={onEndpointChange}
                />
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
};
